import { FiLoader } from 'react-icons/fi';
import { cn } from '@/shared/lib';
import type { ButtonProps } from './types';
import { buttonSpinnerStyles, buttonStyles } from './styles';

export type { ButtonProps, ButtonSize, ButtonVariant } from './types';
export { buttonSpinnerStyles, buttonStyles } from './styles';

/**
 * The application's action control.
 *
 * Deliberately a real `<button>` with `type="button"`, not a `<div>` with a
 * click handler. Two reasons, and neither is negotiable:
 *
 * 1. A button is in the tab order, responds to Enter and Space, and is
 *    discoverable as `button` by assistive technology — all for free.
 * 2. `type="button"` is set explicitly. HTML's default for a button inside a
 *    form is `submit`, so an unspecified type silently submits the enclosing
 *    form. That bug is invisible until a screen has two buttons in one form.
 *
 * `loading` implies disabled, so a control already doing work cannot be
 * submitted twice.
 */
export function Button({
  variant,
  size,
  loading = false,
  disabled = false,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonStyles({ variant, size }), className)}
    >
      {loading ? <FiLoader aria-hidden="true" className={buttonSpinnerStyles} /> : null}
      {children}
    </button>
  );
}